import { z } from "zod";
import type { SearchFilters } from "@/lib/api/types";
import type { StageItem } from "@/features/jobs/components/stage-cards";
import {
  PRESET_TYPE_SLUGS,
  type PresetTypeSlug,
  getPresetMeta,
} from "../lib/categories";
import {
  buildDefaultStagePresetStages,
  splitStagesForPreset,
} from "../lib/stage-preset";

export type Visibility = "public" | "private";

export interface EntityItem {
  /** React Hook Form / list key — may be a random UUID and must not be sent as canonical_id. */
  id: string;
  name: string;
  domain?: string;
  /** Real company canonical UUID when known (e.g. company detail). */
  canonical_id?: string;
}

const entityItemSchema = z.object({
  id: z.string(),
  name: z.string().min(1),
  domain: z.string().optional(),
  canonical_id: z.string().optional(),
});

const stageItemSchema = z.object({
  id: z.string(),
  name: z.string().min(1),
  color: z.string().min(1),
  description: z.string(),
  is_system: z.boolean(),
});

export const presetFormSchema = z
  .object({
    presetType: z.enum(PRESET_TYPE_SLUGS),
    title: z
      .string()
      .trim()
      .min(1, "A title is required.")
      .max(120, "Title must be 120 characters or fewer."),
    description: z
      .string()
      .max(500, "Description must be 500 characters or fewer.")
      .optional(),
    visibility: z.enum(["public", "private"]),
    entities: z.array(entityItemSchema),
    stages: z.array(stageItemSchema),
  })
  .superRefine((data, ctx) => {
    if (data.presetType === "filters") return;

    if (data.presetType === "job-stages") {
      const { editable } = splitStagesForPreset(data.stages);
      if (editable.length === 0) {
        ctx.addIssue({
          code: "custom",
          path: ["stages"],
          message: "Add at least one custom stage to enable saving.",
        });
      }
      return;
    }

    if (data.entities.length === 0) {
      const noun = getPresetMeta(data.presetType).entityNoun.replace(/s$/, "");
      ctx.addIssue({
        code: "custom",
        path: ["entities"],
        message: `Add at least one ${noun} to enable saving.`,
      });
    }
  });

export type PresetFormValues = z.infer<typeof presetFormSchema>;

export interface PresetInitialData {
  title?: string;
  description?: string;
  visibility?: Visibility;
  entities?: EntityItem[];
  filters?: SearchFilters;
  stages?: StageItem[];
}

/** Preset-form defaults. `buildDefaultValues` also exists in the snippets and
 *  tasks form schemas: the name is shared feature-form convention, but each
 *  builds its own feature's defaults — nothing to consolidate (ARBI-851). */
export function buildDefaultValues(
  presetType: PresetTypeSlug,
  initialData?: PresetInitialData,
): PresetFormValues {
  return {
    presetType,
    title: initialData?.title ?? "",
    description: initialData?.description ?? "",
    visibility: initialData?.visibility ?? "public",
    entities: initialData?.entities ?? [],
    stages: initialData?.stages ?? buildDefaultStagePresetStages(),
  };
}

const ENTITY_CANONICAL_ID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** Map form entities to API preset items. Only explicit canonical_id is sent — never form id. */
export function serializePresetEntities(entities: EntityItem[]) {
  return entities.map((entity, index) => {
    const item: {
      name: string;
      domain?: string;
      sort_order: number;
      canonical_id?: string;
    } = {
      name: entity.name,
      domain: entity.domain,
      sort_order: index,
    };
    if (
      entity.canonical_id &&
      ENTITY_CANONICAL_ID_RE.test(entity.canonical_id)
    ) {
      item.canonical_id = entity.canonical_id;
    }
    return item;
  });
}

export type { PresetTypeSlug };
